import React, { useState } from "react";
import { toLocalDateString } from "../utils/dateUtils";
import { useEntryAccent } from "../hooks/useEntryAccent";

const KEYPAD = ["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "backspace"];

/**
 * The numpad entry form, shared by income and expenses.
 *
 * Field state lives here rather than being lifted: nothing outside this
 * component reads it. Note that the sheet keeps this mounted across tab
 * switches, but remounts it via a `key` when the entry kind changes, so a
 * half-typed amount can't carry over into the other table.
 *
 * @param {Object} props
 * @param {import('../constants/entryKinds').EntryKind} props.kind
 * @param {string[]} props.categories
 * @param {(entry: Object) => Promise<any>} props.onSubmit
 *   Must reject on failure — this component keeps the entered values and shows
 *   an error when it does, and the caller relies on that to decide whether to
 *   close the sheet.
 */
const EntryForm = ({ kind, categories, onSubmit }) => {
  const accent = useEntryAccent(kind);
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("0");
  const [description, setDescription] = useState("");
  // Income only: the money is earned but hasn't landed yet, so it goes to
  // Outstanding instead of into this month's totals.
  const [unpaid, setUnpaid] = useState(false);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  const addEntry = async () => {
    const parsedAmount = Number(amount);

    if (!category) {
      setStatus({ type: "error", text: kind.missingCategoryText });
      return;
    }
    if (!amount || Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setStatus({ type: "error", text: "Enter an amount greater than zero." });
      return;
    }

    setSaving(true);
    setStatus(null);
    try {
      // The date is the user's local calendar date at the moment of entry, and
      // is stored as-is. It stays anchored to that day regardless of any later
      // timezone change.
      const today = toLocalDateString();
      const entry = {
        category,
        amount: parsedAmount,
        description,
        // Unpaid income has no cash-intake date yet; that's what puts it in
        // Outstanding and keeps it out of every month total.
        date: kind.hasEarnedDate && unpaid ? null : today,
      };
      if (kind.hasEarnedDate) entry.earnedDate = today;

      await onSubmit(entry);

      setCategory("");
      setAmount("0");
      setDescription("");
      setUnpaid(false);
      setStatus({ type: "success", text: kind.successText });
    } catch (error) {
      // Values are deliberately left in place so the user can retry without
      // re-entering everything.
      console.error(error);
      setStatus({ type: "error", text: kind.failureText });
    } finally {
      setSaving(false);
    }
  };

  const handleButtonClick = (value) => {
    setStatus(null);

    if (value === "backspace") {
      setAmount(amount.length <= 1 ? "0" : amount.slice(0, -1));
      return;
    }
    // A second decimal point would make the value unparseable, and parseFloat
    // would silently truncate it ("1.2.3" -> 1.2) rather than reject it.
    if (value === "." && amount.includes(".")) {
      return;
    }
    // Cap the fractional part at cents.
    const [, fraction] = amount.split(".");
    if (value !== "." && fraction !== undefined && fraction.length >= 2) {
      return;
    }
    if (value === ".") {
      setAmount(`${amount}.`);
      return;
    }
    setAmount(amount === "0" ? value : amount + value);
  };

  const handleCategoryClick = (c) => {
    setCategory(category === c ? "" : c);
  };

  return (
    <div className="p-4 pt-1">
      {/* Category chips */}
      {categories.length === 0 ? (
        <p className="mb-5 text-sm text-gray-500 bg-white border border-gray-200 rounded-xl p-3">
          {kind.noCategoriesHint}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2 mb-5">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => handleCategoryClick(c)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                category === c
                  ? `bg-gradient-to-r ${accent.button} text-white shadow-lg ${accent.shadow} scale-105`
                  : "bg-white text-gray-600 hover:bg-gray-100 shadow-sm border border-gray-200 hover:border-gray-300"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {/* Amount display */}
      <div className="mb-5">
        <div className="bg-white rounded-2xl border border-gray-100 p-4 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Amount</p>
          <p className="text-4xl font-bold text-gray-800">
            <span className="text-gray-400">$</span>{amount}
          </p>
        </div>
      </div>

      {/* Numpad */}
      <div className="mb-4 grid grid-cols-3 gap-2">
        {KEYPAD.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => handleButtonClick(key)}
            className={`p-4 rounded-xl text-lg font-semibold transition-all duration-150 active:scale-95 ${
              key === "backspace"
                ? "bg-gray-200 text-gray-600 hover:bg-gray-300"
                : "bg-white text-gray-700 hover:bg-gray-100 shadow-sm border border-gray-100"
            }`}
          >
            {key === "backspace" ? "⌫" : key}
          </button>
        ))}
      </div>

      {/* Description input */}
      <div className="mb-4">
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Add a note (optional)"
          className="w-full p-4 bg-white border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent transition-all"
        />
      </div>

      {kind.supportsOutstanding && (
        <label className="mb-4 flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl cursor-pointer">
          <input
            type="checkbox"
            checked={unpaid}
            onChange={(e) => setUnpaid(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span className="text-sm text-gray-700">
            Not paid yet
            <span className="block text-xs text-gray-400">
              Tracks it under Outstanding until the money lands.
            </span>
          </span>
        </label>
      )}

      {status && (
        <div
          role="status"
          className={`mb-4 p-3 rounded-xl text-sm border ${
            status.type === "error"
              ? "bg-red-50 border-red-200 text-red-700"
              : "bg-emerald-50 border-emerald-200 text-emerald-700"
          }`}
        >
          {status.text}
        </div>
      )}

      {/* Submit button */}
      <button
        type="button"
        onClick={addEntry}
        disabled={saving}
        className={`w-full p-4 rounded-xl text-white font-semibold bg-gradient-to-r ${accent.button} ${accent.buttonHover} shadow-lg ${accent.shadow} disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98]`}
      >
        {saving ? kind.addingLabel : kind.addLabel}
      </button>
    </div>
  );
};

export default EntryForm;
